import { Panel } from '../core/Panel';
import {
  Sim, TRUCK_TRAVEL_TIME, TRUCK_CARGO_CAP,
  PRICES, SEED_COSTS, SEED_TYPES, STONE_BUY_PRICE, CARGO_KINDS, emptyCargo,
  CargoKind,
} from '../../game/simulation';
import { ICON } from '../icons';
// Alias the t() helper to tt() since this file already uses `t` as the
// local truck variable in body()/onClick().
import { t as tt, plural } from '../strings';

/** Rendering metadata per cargo kind — label + glyph + inventory accessor. */
type CargoRow = {
  kind: CargoKind;
  label: string;
  glyph: string;
  /** How much of this kind is currently in player storage. */
  have: (r: any) => number;
};
const CARGO_ROWS: CargoRow[] = [
  { kind: 'wheat',     label: 'Wheat',      glyph: ICON.wheat,    have: r => r.basket },
  { kind: 'corn',      label: 'Corn',       glyph: ICON.corn,     have: r => r.corn },
  { kind: 'carrot',    label: 'Carrot',     glyph: ICON.carrot,   have: r => r.carrot },
  { kind: 'flour',     label: 'Flour',      glyph: ICON.flour,    have: r => r.flour },
  { kind: 'cornFlour', label: 'Corn flour', glyph: ICON.flour,    have: r => r.cornFlour ?? 0 },
  { kind: 'wood',      label: 'Wood',       glyph: ICON.wood,     have: r => r.wood },
  { kind: 'mushroom',  label: 'Mushroom',   glyph: ICON.mushroom, have: r => r.mushrooms ?? 0 },
  { kind: 'berry',     label: 'Berry',      glyph: ICON.berry,    have: r => r.berries ?? 0 },
  { kind: 'acorn',     label: 'Acorn',      glyph: ICON.acorn,    have: r => r.treeSeeds ?? 0 },
];

export class TruckPanel extends Panel {
  constructor(sim: Sim) { super(sim); }
  id() { return 'truck'; }
  title() { return 'Truck'; }
  hashInputs() {
    const t = this.sim.state.truck;
    const r = this.sim.state.res;
    return JSON.stringify({
      status: t.status,
      timer: Math.floor(t.timer),
      nextArrival: Math.floor(t.nextArrival),
      cargo: t.cargo,
      platform: t.platform,
      payload: t.returnPayload,
      basket: r.basket, corn: r.corn, carrot: r.carrot, flour: r.flour, wood: r.wood,
      cornFlour: r.cornFlour ?? 0,
      mushrooms: r.mushrooms ?? 0, berries: r.berries ?? 0, treeSeeds: r.treeSeeds ?? 0,
      money: r.money,
      seeds: r.seeds,
      stone: r.stone ?? 0,
      pending: (t.pendingSeeds ?? []).map(o => `${o.seed}:${o.qty}`).join(','),
      pendingStone: t.pendingStone?.qty ?? 0,
    });
  }
  body() {
    const t = this.sim.state.truck;
    const r = this.sim.state.res;
    const stateLabel = ({
      idle:      tt('truck.status.idle'),
      loading:   tt('truck.status.loading'),
      departed:  tt('truck.status.departed',  { secs: Math.ceil(t.timer) }),
      returning: tt('truck.status.returning', { coins: t.returnPayload, secs: Math.ceil(t.timer) }),
    })[t.status];
    // The truck is loadable + dispatchable whenever it's parked, no
    // "wait for the call" friction. Only `departed` / `returning` lock it.
    const canLoad = t.status === 'idle' || t.status === 'loading';
    const plat = t.platform ?? emptyCargo();
    // Mid-trip the truck is unloadable — buttons disable + explain.
    const truckAway = t.status === 'departed' || t.status === 'returning';
    const loadTooltip = truckAway ? tt('truck.btn.load.tooltipAway') : '';
    const renderRow = (cr: CargoRow) => {
      const price = PRICES[cr.kind];
      const onTruck = t.cargo[cr.kind] ?? 0;
      const onPlatform = plat[cr.kind] ?? 0;
      const have = cr.have(r);
      if (have === 0 && onTruck === 0 && onPlatform === 0) return '';
      return `
      <div class="ui-cargo">
        <div class="ui-cargo__label">
          <span class="glyph glyph-inline">${cr.glyph}</span> ${cr.label}
          <span class="ui-cargo__price">${tt('truck.cargo.price', { price })}</span>
        </div>
        <div class="ui-cargo__counts">
          ${tt('truck.row.storage', { n: have })}<br>
          ${onPlatform > 0 ? `${tt('truck.row.queued', { n: onPlatform })} <span class="ui-cargo__sub">= ${onPlatform * price}c</span><br>` : ''}${onTruck > 0 ? `${tt('truck.row.onTruck', { n: onTruck })} <span class="ui-cargo__sub">= ${onTruck * price}c</span>` : ''}
        </div>
        <div class="ui-cargo__btns">
          <button class="ui-cargo__btn" data-platform-all="${cr.kind}" ${have > 0 ? '' : 'disabled'} ${loadTooltip ? `title="${loadTooltip}"` : ''}>${tt('truck.btn.loadAll')}</button>
          <button class="ui-cargo__btn" data-platform="${cr.kind}"     ${have > 0 ? '' : 'disabled'} ${loadTooltip ? `title="${loadTooltip}"` : ''}>${tt('truck.btn.loadOne')}</button>
          <button class="ui-cargo__btn" data-recall="${cr.kind}" ${onPlatform > 0 ? '' : 'disabled'} title="${tt('truck.btn.recall.title')}">${tt('truck.btn.recall')}</button>
        </div>
      </div>`;
    };
    let cargoTotal = 0;
    let platformTotal = 0;
    let cargoPayout = 0;
    let platformPayout = 0;
    for (const k of CARGO_KINDS) {
      const ct = t.cargo[k] ?? 0;
      const pt = plat[k] ?? 0;
      cargoTotal += ct;
      platformTotal += pt;
      cargoPayout += ct * PRICES[k];
      platformPayout += pt * PRICES[k];
    }
    const rowsHTML = CARGO_ROWS.map(renderRow).join('');
    return `
      <div class="ui-section">
        <div class="ui-section__title">Status</div>
        <div>${stateLabel}</div>
      </div>
      <div class="ui-tip">${tt('truck.tip.intro')}</div>
      ${rowsHTML || `<div class="ui-tip">${tt('truck.tip.empty')}</div>`}
      ${platformTotal > 0 ? `
      <div class="ui-row" title="${tt('truck.payout.title.withQueue', { n: platformTotal, items: 'item' + plural(platformTotal) })}">
        <strong>${tt('truck.queue.label')}</strong>
        <strong>${tt('truck.queue.value', { n: platformTotal, items: 'item' + plural(platformTotal), coins: platformPayout })}</strong>
      </div>` : ''}
      <div class="ui-row">
        <strong>${tt('truck.cargo.label')}</strong>
        <strong>${cargoTotal === 0 ? tt('truck.cargo.empty') : tt('truck.cargo.value', { n: cargoTotal, cap: TRUCK_CARGO_CAP, coins: cargoPayout })}</strong>
      </div>
      ${(t.pendingSeeds ?? []).length > 0 ? `
      <div class="ui-row"><strong>${tt('truck.seedErrand.label')}</strong><strong>${(t.pendingSeeds ?? []).map(o => `${o.qty} ${o.seed}`).join(', ')}</strong></div>
      ` : ''}
      ${t.pendingStone && t.pendingStone.qty > 0 ? `
      <div class="ui-row"><strong>${tt('truck.stoneErrand.label')}</strong><strong>${t.pendingStone.qty} stone</strong></div>
      ` : ''}
      <div class="ui-row ui-row--accent" title="${platformTotal > 0 ? tt('truck.payout.title.withQueue', { n: platformTotal, items: 'item' + plural(platformTotal) }) : tt('truck.payout.title.truckOnly')}">
        <strong>${tt('truck.payout.label')}</strong>
        <strong>${platformTotal > 0
          ? tt('truck.payout.valueQueue', { coins: platformPayout + cargoPayout })
          : tt('truck.payout.value',      { coins: platformPayout + cargoPayout })}</strong>
      </div>
      <button class="ui-btn ui-btn--primary ui-btn--block" data-act="dispatch" ${canLoad && (cargoTotal + platformTotal + (t.pendingSeeds ?? []).length + (t.pendingStone?.qty ?? 0)) > 0 ? '' : 'disabled'}>
        ${tt('truck.send.btn', { secs: TRUCK_TRAVEL_TIME * 2 })}
      </button>
      <div class="ui-section">
        <div class="ui-section__title">${tt('truck.section.buySeeds')}</div>
        <div class="ui-tip">${tt('truck.section.buySeedsTip')}</div>
        ${SEED_TYPES.map(k => `
          <div class="ui-cargo">
            <div class="ui-cargo__label">
              <span class="glyph glyph-inline">${k === 'wheat' ? ICON.wheat : k === 'corn' ? ICON.corn : ICON.carrot}</span> ${k}
              <span class="ui-cargo__price">${tt('truck.seed.price', { price: SEED_COSTS[k] })}</span>
            </div>
            <div class="ui-cargo__counts">
              ${tt('truck.seed.row.stock', { n: r.seeds[k] ?? 0 })}
            </div>
            <div class="ui-cargo__btns">
              <button class="ui-cargo__btn" data-buyseed="${k}" data-qty="1"  ${r.money < SEED_COSTS[k] ? 'disabled' : ''}>${tt('truck.seed.btn.qty', { qty: 1,  cost: SEED_COSTS[k] })}</button>
              <button class="ui-cargo__btn" data-buyseed="${k}" data-qty="10" ${r.money < SEED_COSTS[k]*10 ? 'disabled' : ''}>${tt('truck.seed.btn.qty', { qty: 10, cost: SEED_COSTS[k] * 10 })}</button>
              <button class="ui-cargo__btn" data-buyseed="${k}" data-qty="50" ${r.money < SEED_COSTS[k]*50 ? 'disabled' : ''}>${tt('truck.seed.btn.qty', { qty: 50, cost: SEED_COSTS[k] * 50 })}</button>
            </div>
          </div>`).join('')}
      </div>
      <div class="ui-section">
        <div class="ui-section__title">${tt('truck.section.buyStone')}</div>
        <div class="ui-tip">${tt('truck.section.buyStoneTip')}</div>
        <div class="ui-cargo">
          <div class="ui-cargo__label">
            <span class="glyph glyph-inline">${ICON.stone ?? ''}</span> Stone
            <span class="ui-cargo__price">${tt('truck.stone.price', { price: STONE_BUY_PRICE })}</span>
          </div>
          <div class="ui-cargo__counts">
            ${tt('truck.stone.row.stock', { n: r.stone ?? 0 })}
          </div>
          <div class="ui-cargo__btns">
            <button class="ui-cargo__btn" data-buystone="1"  ${r.money < STONE_BUY_PRICE      ? 'disabled' : ''}>${tt('truck.stone.btn.qty', { qty: 1,  cost: STONE_BUY_PRICE })}</button>
            <button class="ui-cargo__btn" data-buystone="5"  ${r.money < STONE_BUY_PRICE * 5  ? 'disabled' : ''}>${tt('truck.stone.btn.qty', { qty: 5,  cost: STONE_BUY_PRICE * 5 })}</button>
            <button class="ui-cargo__btn" data-buystone="10" ${r.money < STONE_BUY_PRICE * 10 ? 'disabled' : ''}>${tt('truck.stone.btn.qty', { qty: 10, cost: STONE_BUY_PRICE * 10 })}</button>
          </div>
        </div>
      </div>
    `;
  }
  onClick(e: Event) {
    const t = e.target as HTMLElement;
    const plat   = (t.closest('[data-platform]')      as HTMLElement | null)?.dataset.platform     as CargoKind | undefined;
    const platAll= (t.closest('[data-platform-all]')  as HTMLElement | null)?.dataset.platformAll  as CargoKind | undefined;
    const recall = (t.closest('[data-recall]')        as HTMLElement | null)?.dataset.recall       as CargoKind | undefined;
    const load   = (t.closest('[data-load]')          as HTMLElement | null)?.dataset.load         as CargoKind | undefined;
    const buyEl  = (t.closest('[data-buyseed]')       as HTMLElement | null);
    if (plat)    { this.sim.sendToPlatform(plat);       return; }
    if (platAll) { this.sim.sendAllToPlatform(platAll); return; }
    if (recall)  { this.sim.recallFromPlatform(recall); return; }
    if (load)    { this.sim.loadTruck(load);            return; }
    if (buyEl) {
      const kind = buyEl.dataset.buyseed as 'wheat' | 'corn' | 'carrot';
      const qty = parseInt(buyEl.dataset.qty ?? '1', 10) || 1;
      this.sim.buySeedsDirect(kind, qty);
      return;
    }
    const buyStoneEl = (t.closest('[data-buystone]') as HTMLElement | null);
    if (buyStoneEl) {
      const qty = parseInt(buyStoneEl.dataset.buystone ?? '1', 10) || 1;
      this.sim.buyStoneDirect(qty);
      return;
    }
    const actEl = t.closest('[data-act]') as HTMLElement | null;
    if (actEl?.dataset.act === 'dispatch' && !(actEl as HTMLButtonElement).disabled) {
      this.sim.dispatchTruck();
    }
  }
}
